/**
 * Client-side cryptographic utilities for zero-knowledge encryption.
 * 
 * This module handles:
 * - Password-derived key generation (PBKDF2)
 * - Private key decryption (AES-GCM)
 * 
 * The private RSA key is decrypted client-side and NEVER sent to the server
 * in plaintext. Only the encrypted form is stored server-side.
 */

import CryptoJS from 'crypto-js';
import { clearVaultCache } from './vault';
import { announceUnlocked } from './keyShare';

/**
 * Derive a key from password using PBKDF2.
 * Must match the backend implementation.
 * 
 * @param {string} password - User's password
 * @param {string} saltBase64 - Base64 encoded salt from server
 * @returns {string} - Base64 encoded 32-byte key
 */
export function deriveKeyFromPassword(password, saltBase64) {
  // Decode salt from base64
  const salt = CryptoJS.enc.Base64.parse(saltBase64);
  
  // PBKDF2 with SHA-256, 100000 iterations, 256 bits (32 bytes)
  const key = CryptoJS.PBKDF2(password, salt, {
    keySize: 256 / 32,  // 8 words = 32 bytes
    iterations: 100000,
    hasher: CryptoJS.algo.SHA256
  });
  
  return key.toString(CryptoJS.enc.Base64);
}

/**
 * Decrypt the private key using AES-GCM.
 * The encrypted blob format is: nonce (16 bytes) + tag (16 bytes) + ciphertext
 * 
 * Note: CryptoJS doesn't support GCM, so we need to use the Web Crypto API
 * for proper GCM decryption.
 * 
 * @param {string} encryptedPrivateKeyBase64 - Base64 encoded encrypted private key
 * @param {string} pdkBase64 - Base64 encoded password-derived key
 * @returns {Promise<string>} - Decrypted private key (PEM format)
 */
export async function decryptPrivateKey(encryptedPrivateKeyBase64, pdkBase64) {
  // Decode from base64
  const encryptedBlob = Uint8Array.from(atob(encryptedPrivateKeyBase64), c => c.charCodeAt(0));
  const pdk = Uint8Array.from(atob(pdkBase64), c => c.charCodeAt(0));
  
  // Extract nonce (16 bytes), tag (16 bytes), and ciphertext
  const nonce = encryptedBlob.slice(0, 16);
  const tag = encryptedBlob.slice(16, 32);
  const ciphertext = encryptedBlob.slice(32);
  
  // Combine ciphertext and tag for Web Crypto API (it expects tag appended)
  const ciphertextWithTag = new Uint8Array(ciphertext.length + tag.length);
  ciphertextWithTag.set(ciphertext);
  ciphertextWithTag.set(tag, ciphertext.length);
  
  // Import the key
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    pdk,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
  
  // Decrypt
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce },
    cryptoKey,
    ciphertextWithTag
  );
  
  // Convert to string (PEM format)
  return new TextDecoder().decode(decrypted);
}

/**
 * Store encryption keys securely in session storage.
 * 
 * @param {object} keys - Object containing encryption keys
 * @param {string} keys.privateKey - Decrypted private key (PEM)
 * @param {string} keys.encryptionKey - Legacy symmetric key (for backward compatibility)
 */
export function storeEncryptionKeys(keys) {
  if (keys.privateKey) {
    sessionStorage.setItem('private_key', keys.privateKey);
  }
  if (keys.encryptionKey) {
    sessionStorage.setItem('encryption_key', keys.encryptionKey);
  }
  // A tab waiting on the password prompt can take it from here now.
  if (keys.privateKey) announceUnlocked();
}

/**
 * Get the stored private key.
 * @returns {string|null} - Private key PEM or null if not available
 */
export function getPrivateKey() {
  return sessionStorage.getItem('private_key');
}

/**
 * Replace the active private key in session storage. Used when stepping
 * into a shared account so existing decrypt flows continue to work.
 * @param {string} privateKeyPem
 */
export function setPrivateKey(privateKeyPem) {
  if (privateKeyPem) sessionStorage.setItem('private_key', privateKeyPem);
}

/**
 * Get the stored legacy encryption key.
 * @returns {string|null} - Legacy key or null if not available
 */
export function getEncryptionKey() {
  return sessionStorage.getItem('encryption_key');
}

/**
 * Clear all encryption keys from session storage.
 */
export function clearEncryptionKeys() {
  clearVaultCache();
  sessionStorage.removeItem('private_key');
  sessionStorage.removeItem('encryption_key');
}

/**
 * Check if encryption keys are available.
 * @returns {boolean}
 */
export function hasEncryptionKeys() {
  return !!(sessionStorage.getItem('private_key') || sessionStorage.getItem('encryption_key'));
}
